import React from "react";
import { motion } from "framer-motion";

const PartnerBenefits = () => {
  const items = [
    {
      title: "Infrastructure Integration",
      desc: "Integrate your strategies, liquidity systems, or custody frameworks directly into QNC’s execution layer, powered by Enzyme’s audited smart-contract architecture. This enables automated execution, transparent performance tracking, and seamless interoperability between decentralized and institutional systems.",
      ribbon: "Seamless Integration",
    },
    {
      title: "Revenue Alignment",
      desc: "Partners participate in performance-linked revenue through automated, on-chain fee sharing above the high-watermark — ensuring full incentive alignment and transparent results.",
      ribbon: "On-Chain Incentives",
    },
    {
      title: "Regulatory Transparency",
      desc: "Operate within MiCA and FATF-aligned frameworks through QNC’s non-custodial infrastructure, designed for compatibility with qualified-custodian integrations and institutional reporting standards.",
      ribbon: "Compliant Layer",
    },
    {
      title: "Brand Visibility & Distribution",
      desc: "Access QNC’s allocator and investor network through joint visibility initiatives — including co-branded materials, performance highlights, and platform-based strategy placement.",
      ribbon: "Shared Reach",
    },
  ];

  return (
    <section
      id="benefits"
      className="relative py-28 bg-gradient-to-b from-white to-gray-50 overflow-hidden"
    >
      {/* Subtle animated background grid */}
      <div className="absolute inset-0 -z-0 opacity-20 pointer-events-none">
        <svg
          className="w-full h-full animate-[gridmove_60s_linear_infinite]"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path
                d="M50 0H0V50"
                fill="none"
                stroke="#0A2A66"
                strokeOpacity="0.15"
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <style>
        {`
          @keyframes gridmove {
            0% { transform: translate(0, 0); }
            50% { transform: translate(25px, 25px); }
            100% { transform: translate(0, 0); }
          }
        `}
      </style>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section label */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-xs tracking-wider font-semibold uppercase text-cyan-700 bg-cyan-50 px-4 py-1 rounded-full">
            Partnership Advantages
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h2
          className="text-4xl md:text-5xl font-bold text-center mb-16 text-gray-900"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Why Partner with QNC
        </motion.h2>

        {/* Cards with ribbons */}
        <div className="grid md:grid-cols-2 gap-10">
          {items.map((i, idx) => (
            <motion.div
              key={idx}
              className="group relative bg-white p-10 rounded-2xl shadow-md border border-gray-100 overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
            >
              {/* Ribbon */}
              {i.ribbon && (
                <div className="absolute -right-16 top-5 rotate-45">
                  <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-[10px] font-semibold tracking-wide px-16 py-1 shadow-md">
                    {i.ribbon}
                  </span>
                </div>
              )}

              {/* Accent bar animation */}
              <div className="absolute top-0 left-0 h-1 w-0 bg-gradient-to-r from-cyan-500 to-blue-600 group-hover:w-full transition-all duration-500"></div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">{i.title}</h3>
              <p className="text-gray-600 leading-relaxed">{i.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerBenefits;
